
import React from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { DollarSign } from 'lucide-react';
import { Transaction } from '@/types/payment';

interface DashboardProps {
  transactions: Transaction[];
}

const Dashboard: React.FC<DashboardProps> = ({ transactions }) => {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Transactions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{transactions.length}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Volume</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${transactions.reduce((sum, t) => sum + t.amount, 0).toFixed(2)}</div>
          </CardContent>
        </Card>
        
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total Fees</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">${transactions.reduce((sum, t) => sum + t.fee, 0).toFixed(2)}</div>
          </CardContent>
        </Card>
      </div>
      
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>Recent Activity</CardTitle>
          <CardDescription>Latest transactions processed by the system</CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[300px]">
            {transactions.length > 0 ? (
              <div className="space-y-4">
                {[...transactions].slice(0, 5).map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between border-b pb-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-medium">{transaction.recipient}</p>
                        <DollarSign className="h-4 w-4 text-green-500" />
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {new Date(transaction.timestamp).toLocaleString()}
                      </p>
                      <p className="text-xs text-gray-400">
                        From: {transaction.senderEmail}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right">
                        <p className="font-medium">${transaction.amount.toFixed(2)}</p>
                        <p className="text-xs text-gray-500">Fee: ${transaction.fee.toFixed(2)}</p>
                      </div>
                      {transaction.status === 'completed' ? 
                        <Badge variant="outline" className="bg-green-100 text-green-800">Completed</Badge> : 
                        <Badge variant="outline" className="bg-red-100 text-red-800">Failed</Badge>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center py-8 text-muted-foreground">No transactions found</p>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </>
  );
};

export default Dashboard;
